import { addDays, toDateInputString, toIsoString } from './demoUtils';
import { getNotificationKey } from './demoNotifications';

function createIsoOffset(baseDate, daysOffset) {
  return toIsoString(addDays(baseDate, daysOffset));
}

function createDateOffset(baseDate, daysOffset) {
  return toDateInputString(addDays(baseDate, daysOffset));
}

export function buildInitialDemoData(today = new Date()) {
  const insuranceTypes = [
    {
      id: 1,
      name: 'General Liability',
      user_id: 999,
      created_at: toIsoString(today),
      custom_fields: [
        { id: 1, field_name: 'Per Occurrence Limit', field_type: 'number', required: true },
        { id: 2, field_name: 'Aggregate Limit', field_type: 'number', required: true },
      ],
    },
    {
      id: 2,
      name: 'Workers Compensation',
      user_id: 999,
      created_at: toIsoString(today),
      custom_fields: [
        { id: 3, field_name: 'Coverage Limit', field_type: 'number', required: true },
      ],
    },
    {
      id: 3,
      name: 'Professional Liability (E&O)',
      user_id: 999,
      created_at: toIsoString(today),
      custom_fields: [],
    },
    {
      id: 4,
      name: 'Auto Insurance',
      user_id: 999,
      created_at: toIsoString(today),
      custom_fields: [
        { id: 4, field_name: 'Number of Vehicles', field_type: 'number', required: false },
      ],
    },
    {
      id: 5,
      name: 'Umbrella Insurance',
      user_id: 999,
      created_at: toIsoString(today),
      custom_fields: [],
    },
  ];

  const vendors = [
    {
      id: 1,
      name: 'ABC Landscaping',
      contact_name: 'Juan Martinez',
      contact_email: 'juan@abclandscaping.com',
      contact_phone: '(555) 123-4567',
      address: '123 Garden St, Austin, TX 78701',
      status: 'active',
      is_self: false,
      created_at: createIsoOffset(today, -120),
      updated_at: createIsoOffset(today, -120),
    },
    {
      id: 2,
      name: 'Premier Plumbing',
      contact_name: 'Sarah Johnson',
      contact_email: 'sarah@premierplumbing.com',
      contact_phone: '(555) 234-5678',
      address: '456 Water Ave, Austin, TX 78702',
      status: 'active',
      is_self: false,
      created_at: createIsoOffset(today, -110),
      updated_at: createIsoOffset(today, -110),
    },
    {
      id: 3,
      name: 'Elite Electric',
      contact_name: 'Mike Thompson',
      contact_email: 'mike@eliteelectric.com',
      contact_phone: '(555) 345-6789',
      address: '789 Power Blvd, Austin, TX 78703',
      status: 'active',
      is_self: false,
      created_at: createIsoOffset(today, -100),
      updated_at: createIsoOffset(today, -100),
    },
    {
      id: 6,
      name: 'Self',
      contact_name: 'Company Insurance',
      contact_email: 'insurance@mycompany.com',
      contact_phone: '(555) 000-0000',
      address: '100 Main St, Austin, TX 78701',
      status: 'active',
      is_self: true,
      created_at: createIsoOffset(today, -180),
      updated_at: createIsoOffset(today, -180),
    },
  ];

  const policies = [
    {
      id: 1,
      vendor_id: 1,
      insurance_type_id: 1,
      policy_name: 'General Liability',
      policy_number: 'GL-2026-001',
      insurer_name: 'State Farm',
      effective_date: createDateOffset(today, -180),
      expiration_date: createDateOffset(today, 180),
      general_liability_occurrence_limit: 1000000,
      general_liability_aggregate_limit: 2000000,
      workers_comp_limit: null,
      additional_insured_status: true,
      custom_field_values: { 1: '1000000', 2: '2000000' },
      created_at: createIsoOffset(today, -180),
      updated_at: createIsoOffset(today, -180),
    },
    {
      id: 2,
      vendor_id: 1,
      insurance_type_id: 2,
      policy_name: 'Workers Compensation',
      policy_number: 'WC-2026-001',
      insurer_name: 'Liberty Mutual',
      effective_date: createDateOffset(today, -180),
      expiration_date: createDateOffset(today, 180),
      general_liability_occurrence_limit: null,
      general_liability_aggregate_limit: null,
      workers_comp_limit: 500000,
      additional_insured_status: false,
      custom_field_values: { 3: '500000' },
      created_at: createIsoOffset(today, -180),
      updated_at: createIsoOffset(today, -180),
    },
    {
      id: 3,
      vendor_id: 2,
      insurance_type_id: 1,
      policy_name: 'General Liability',
      policy_number: 'GL-2026-002',
      insurer_name: 'Progressive',
      effective_date: createDateOffset(today, -120),
      expiration_date: createDateOffset(today, 14),
      general_liability_occurrence_limit: 2000000,
      general_liability_aggregate_limit: 4000000,
      workers_comp_limit: null,
      additional_insured_status: true,
      custom_field_values: { 1: '2000000', 2: '4000000' },
      created_at: createIsoOffset(today, -120),
      updated_at: createIsoOffset(today, -120),
    },
    {
      id: 4,
      vendor_id: 2,
      insurance_type_id: 2,
      policy_name: 'Workers Compensation',
      policy_number: 'WC-2026-002',
      insurer_name: 'Travelers',
      effective_date: createDateOffset(today, -100),
      expiration_date: createDateOffset(today, 240),
      general_liability_occurrence_limit: null,
      general_liability_aggregate_limit: null,
      workers_comp_limit: 1000000,
      additional_insured_status: false,
      custom_field_values: { 3: '1000000' },
      created_at: createIsoOffset(today, -100),
      updated_at: createIsoOffset(today, -100),
    },
    {
      id: 5,
      vendor_id: 3,
      insurance_type_id: 1,
      policy_name: 'General Liability',
      policy_number: 'GL-2026-003',
      insurer_name: 'Allstate',
      effective_date: createDateOffset(today, -90),
      expiration_date: createDateOffset(today, 30),
      general_liability_occurrence_limit: 1000000,
      general_liability_aggregate_limit: 2000000,
      workers_comp_limit: null,
      additional_insured_status: true,
      custom_field_values: { 1: '1000000', 2: '2000000' },
      created_at: createIsoOffset(today, -90),
      updated_at: createIsoOffset(today, -90),
    },
    {
      id: 6,
      vendor_id: 3,
      insurance_type_id: 3,
      policy_name: 'Professional Liability',
      policy_number: 'PL-2026-001',
      insurer_name: 'Hartford',
      effective_date: createDateOffset(today, -90),
      expiration_date: createDateOffset(today, 320),
      general_liability_occurrence_limit: 1000000,
      general_liability_aggregate_limit: 1000000,
      workers_comp_limit: null,
      additional_insured_status: false,
      custom_field_values: {},
      created_at: createIsoOffset(today, -90),
      updated_at: createIsoOffset(today, -90),
    },
    {
      id: 11,
      vendor_id: 3,
      insurance_type_id: 2,
      policy_name: 'Workers Compensation',
      policy_number: 'WC-2025-003',
      insurer_name: 'Old Policy Insurance',
      effective_date: createDateOffset(today, -370),
      expiration_date: createDateOffset(today, -5),
      general_liability_occurrence_limit: null,
      general_liability_aggregate_limit: null,
      workers_comp_limit: 500000,
      additional_insured_status: false,
      custom_field_values: { 3: '500000' },
      created_at: createIsoOffset(today, -370),
      updated_at: createIsoOffset(today, -370),
    },
    {
      id: 12,
      vendor_id: 6,
      insurance_type_id: 1,
      policy_name: 'General Liability',
      policy_number: 'GL-SELF-2026',
      insurer_name: 'Chubb',
      effective_date: createDateOffset(today, -150),
      expiration_date: createDateOffset(today, 150),
      general_liability_occurrence_limit: 5000000,
      general_liability_aggregate_limit: 10000000,
      workers_comp_limit: null,
      additional_insured_status: false,
      custom_field_values: { 1: '5000000', 2: '10000000' },
      created_at: createIsoOffset(today, -150),
      updated_at: createIsoOffset(today, -150),
    },
  ];

  return {
    insuranceTypes,
    vendors,
    policies,
    readNotificationKeys: [getNotificationKey(11, 0)],
  };
}
